import { useEffect, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { isProblemError } from '../../api/client';
import {
  getListTeachersQueryKey,
  listTeachers,
} from '../../api/generated/enrollment/enrollment';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  getListPayrollBatchesQueryKey,
  listPayrollBatches,
  useApproveBatch,
  useAttachBatchReceipt,
  useConfirmBatchReceipt,
  useCreateItemAdjustment,
  useCreatePayrollBatch,
  useGetPayrollBatch,
  usePayBatch,
  useScheduleBatch,
  useSubmitBatchForReview,
} from '../../api/generated/payroll/payroll';
import {
  SettlementMethod,
  type Me,
  type PayrollBatch as PayrollBatchModel,
  type PayrollBatchState,
  type PayrollItemView,
  type TeacherAdminDetail,
} from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { ConfirmDialog } from '../../design/primitives/Dialog';
import { Field } from '../../design/primitives/Field';
import { formatDateInPersonTimezone } from '../../i18n/datetime';
import { formatMoney } from '../billing/money';
// O select com rotulo associado ja existe na SPA; promove-lo a primitivo de
// design e decisao de quem cuida do design system, e nao efeito colateral desta
// tela — mas duplicar o componente aqui seria a segunda copia da mesma regra de
// acessibilidade.
import { SelectField } from '../enrollment/SelectField';
// Os bytes do comprovante vao para o object storage por uma URL que o proprio
// servidor assinou, e nao para a API. E o mesmo `PUT` do anexo da Onda 5, e
// reusa-lo evita uma segunda excecao de `fetch` no `eslint.config.js` para
// fazer exatamente a mesma coisa.
import { uploadToSignedUrl } from '../messaging/signedUpload';
import { isReauthRequired, useReauthGate, type ReauthGate } from '../reauth/ReauthGate';
import { PayrollItemCard } from './TeacherEarnings';
import { currentPeriod, isPeriod } from './period';
import styles from './payroll.module.css';

/**
 * Os seis estados em fila, sem pular etapa.
 *
 * De `scheduled` em diante nao ha volta: dinheiro agendado que volta atras vira
 * dois pagamentos. A tela mostra a fila inteira sempre, e nao so a etapa
 * corrente, porque quem faz o fechamento precisa ver o que ja passou e o que
 * falta.
 */
const BATCH_STATES: readonly PayrollBatchState[] = [
  'accruing',
  'in_review',
  'approved',
  'scheduled',
  'paid',
  'settled',
];

function isProvisionalRate(error: unknown): boolean {
  return isProblemError(error) && error.status === 503;
}

/**
 * Alertas que toda acao sensivel do lote compartilha.
 *
 * O 403 de reautenticacao **nao** aparece aqui: quem responde por ele e o
 * portao, que confirma a senha e repete a acao de onde parou. Um alerta ao lado
 * do dialogo diria duas vezes a mesma coisa, e a versao em texto era justamente
 * a que mandava sair e entrar de novo — perdendo o formulario.
 */
function ActionFeedback({ error }: { readonly error: unknown }) {
  const { t } = useTranslation();
  if (!error || isReauthRequired(error)) return null;

  if (isProvisionalRate(error)) {
    return (
      <Alert tone="warning" title={t('payroll.provisionalRate.title')}>
        {t('payroll.provisionalRate.detail')}
      </Alert>
    );
  }
  return <Alert tone="error">{t('error.unexpected')}</Alert>;
}

/**
 * O maior `limit` que o servidor de folha honra de fato.
 *
 * O contrato declara maximo 200, mas `ListBatches` devolve qualquer valor acima
 * de 100 para o default de 50 (`api/internal/payroll/batch.go`:
 * `if f.Limit <= 0 || f.Limit > 100 { f.Limit = 50 }`). Pedir 200 aqui traria
 * METADE dos lotes por ida ao servidor — 100 e o teto que chega inteiro do
 * outro lado. Pedir o maximo nao substitui seguir o cursor: ele so faz a
 * competencia de uma escola caber em menos idas e voltas.
 */
const BATCH_PAGE_SIZE = 100;

/** Lista de lotes de uma competencia, e a abertura de um lote novo. */
export function PayrollBatchesPage() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;
  // Um portao por tela, e nao por acao: duas acoes recusadas ao mesmo tempo
  // abririam dois dialogos empilhados, e o segundo esconderia o primeiro.
  const gate = useReauthGate();

  const [period, setPeriod] = useState(currentPeriod);
  const valid = isPeriod(period);

  /**
   * A LISTA DE LOTES SEGUE O CURSOR, e nao seguia.
   *
   * `listPayrollBatches` responde no maximo `limit` por chamada e devolve
   * `nextCursor`; pedir sem cursor trazia a primeira pagina e nada mais, sem
   * nenhum sinal na tela de que havia continuacao. Uma competencia tem um lote
   * por professor, e enquanto o campo de competencia esta invalido a lista e o
   * historico inteiro — do 51o lote em diante nada disso existia para a
   * interface: nao havia como submeter, aprovar, agendar nem pagar aquele
   * professor, e o fechamento do mes travava em silencio.
   *
   * E o mesmo desfecho que o comentario do `BatchOpener`, uma secao abaixo,
   * descreve para o seletor de professor. A tela ganhou a lista completa dos
   * professores e continuava sem a lista completa dos lotes que ela abre.
   */
  const list = useInfiniteQuery({
    queryKey: getListPayrollBatchesQueryKey({
      ...(valid ? { period } : {}),
      limit: BATCH_PAGE_SIZE,
    }),
    queryFn: ({ pageParam, signal }) =>
      listPayrollBatches(
        {
          ...(valid ? { period } : {}),
          limit: BATCH_PAGE_SIZE,
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!me,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = list;
  useEffect(() => {
    // Mesma condicao de parada do `BatchOpener`, e pela mesma razao: sem
    // `isFetchNextPageError`, a pagina que falha no meio deixa `hasNextPage`
    // verdadeiro e `isFetchingNextPage` de volta em falso, o efeito dispara de
    // novo e a tela entra em tempestade de requisicoes contra o servidor que
    // acabou de recusar.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  if (!me) return null;

  const batches = (list.data?.pages ?? []).flatMap((page) => page.items);
  // `isError` fala so da primeira pagina; a que falha NO MEIO deixaria a lista
  // truncada sem uma linha na tela dizendo por que.
  const failed = list.isError || isFetchNextPageError;
  // Enquanto ha cursor por seguir, a lista ainda nao e a lista. Dizer
  // "nenhum lote nesta competencia" com pagina por buscar e afirmar sobre o
  // servidor algo que ninguem perguntou a ele — e e a afirmacao que faz quem
  // fecha o mes parar de procurar.
  const loading = !failed && (list.data === undefined || hasNextPage);

  return (
    <div className={styles.page}>
      <h1>{t('payroll.batches.title')}</h1>

      {gate.dialog}

      <div className={styles.filters}>
        <Field
          label={t('payroll.period')}
          placeholder={t('payroll.periodPlaceholder')}
          value={period}
          onChange={(event) => setPeriod(event.target.value)}
          error={valid ? undefined : t('payroll.periodInvalid')}
        />
      </div>

      <BatchOpener
        period={valid ? period : currentPeriod()}
        gate={gate}
        onCreated={() => void list.refetch()}
      />

      {failed ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      <section aria-label={t('payroll.batches.title')}>
        {loading ? <p className={styles.meta}>{t('app.loading')}</p> : null}
        {!loading && !failed && batches.length === 0 ? (
          <p className={styles.meta}>{t('payroll.batches.empty')}</p>
        ) : null}
        {batches.length > 0 ? (
          <ul className={styles.list}>
            {batches.map((batch) => (
              <li key={batch.id} className={styles.card}>
                <p>
                  <Link to={`/payroll/${batch.id}`}>
                    {t('payroll.batches.link', {
                      period: batch.period,
                      teacher: batch.teacher.displayName,
                    })}
                  </Link>
                </p>
                <p>
                  <strong>{formatMoney(batch.totalAmount, me.locale)}</strong> ·{' '}
                  <span>{t(`payroll.batchState.${batch.state}`)}</span> ·{' '}
                  <span>{t('payroll.batches.itemCount', { count: batch.itemCount })}</span>
                </p>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

/**
 * O professor **escolhido da lista**, e nao um identificador digitado.
 *
 * Guardar o `personId` num `string` — que era o que esta tela fazia — mantem
 * exprimivel o estado "identificador sem nome": nele um caractere trocado abre
 * lote do professor errado, e a tela nao tem como confirmar o nome porque nao
 * tem o nome. Guardando a pessoa que `listTeachers` devolveu, so restam dois
 * estados possiveis — ninguem escolhido, ou um professor da lista — e o segundo
 * sempre carrega o nome que vai para a confirmacao.
 */
type ChosenTeacher = Pick<TeacherAdminDetail, 'personId' | 'displayName'>;

/**
 * O teto que o contrato permite pedir de uma vez (`Limit`: maximo 200).
 *
 * Pedir o maximo nao substitui seguir o cursor — ele so faz a lista de uma
 * escola inteira caber em menos idas e voltas.
 */
const TEACHER_PAGE_SIZE = 200;

function BatchOpener({
  period,
  gate,
  onCreated,
}: {
  readonly period: string;
  readonly gate: ReauthGate;
  readonly onCreated: () => void;
}) {
  const { t } = useTranslation();
  /**
   * O quadro inteiro, e nao a primeira pagina dele.
   *
   * A lista nao filtra por `active`: professor que saiu no meio da competencia
   * ainda tem aula dada a pagar, e esconde-lo aqui tornaria o lote dele
   * impossivel de abrir pela interface. Pedir uma pagina so fazia exatamente
   * isso com quem estivesse alem dela — `listTeachers` responde no maximo
   * `limit` por chamada (default 50 no servidor, teto 200 no contrato) e devolve
   * `nextCursor`, e o campo de UUID que este seletor substituiu alcancava
   * qualquer professor. Trocar o campo pela lista sem seguir o cursor tirava
   * alcance da tela em vez de dar: o professor 51 deixava de ter lote.
   *
   * O contrato nao oferece busca no servidor para esta operacao — so `cursor` e
   * `limit` —, entao seguir o cursor ate `nextCursor` nulo e o unico jeito de a
   * lista ser a lista.
   */
  const teachers = useInfiniteQuery({
    queryKey: getListTeachersQueryKey({ limit: TEACHER_PAGE_SIZE }),
    queryFn: ({ pageParam, signal }) =>
      listTeachers(
        pageParam === undefined
          ? { limit: TEACHER_PAGE_SIZE }
          : { limit: TEACHER_PAGE_SIZE, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const create = useCreatePayrollBatch();

  const [chosen, setChosen] = useState<ChosenTeacher | null>(null);
  const [confirming, setConfirming] = useState(false);

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = teachers;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e nao e detalhe:
    // sem ele, a pagina que falha no meio deixa `hasNextPage` verdadeiro e
    // `isFetchingNextPage` de volta em falso, o efeito dispara de novo e a tela
    // entra em tempestade de requisicoes contra o servidor que acabou de
    // recusar.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  // Enquanto ha cursor por seguir a lista ainda esta incompleta, e lista
  // incompleta num seletor nao e "menos opcoes": e o administrador procurando um
  // nome, nao achando, e concluindo que a pessoa nao esta cadastrada — que e o
  // mesmo desfecho do defeito que este ramo existe para fechar.
  const items = hasNextPage ? [] : (teachers.data?.pages ?? []).flatMap((page) => page.items);

  return (
    <section aria-label={t('payroll.batches.open')}>
      <h2>{t('payroll.batches.open')}</h2>
      <div className={styles.actions}>
        <SelectField
          label={t('enrollment.teacher')}
          value={chosen?.personId ?? ''}
          placeholder={t('enrollment.choose')}
          options={items.map((teacher) => ({
            value: teacher.personId,
            label: teacher.displayName,
          }))}
          // O valor do `select` e sempre resolvido de volta contra a lista: o que
          // entra no estado e a pessoa, e nao a string que o evento trouxe.
          onChange={(value) =>
            setChosen(items.find((teacher) => teacher.personId === value) ?? null)
          }
        />
        <Button
          variant="primary"
          disabled={chosen === null}
          loading={create.isPending}
          onClick={() => setConfirming(true)}
        >
          {t('payroll.batches.openAction')}
        </Button>
      </div>

      {/*
        Sem a lista nao ha professor para escolher, e o seletor vazio pareceria
        "nenhum professor cadastrado". O erro precisa aparecer, senao a tela vira
        um beco sem saida silencioso.

        `isFetchNextPageError` entra junto: `isError` fala so da primeira pagina,
        e a pagina que falha NO MEIO do quadro deixaria o seletor vazio para
        sempre, sem uma linha na tela dizendo por que.
      */}
      {teachers.isError || isFetchNextPageError ? (
        <Alert tone="error">{t('error.loadFailed')}</Alert>
      ) : null}
      <ActionFeedback error={create.error} />

      {/*
        A confirmacao existe para dizer o **nome**, e nao para perguntar "tem
        certeza?": e o nome que prova que o lote vai para quem se pretendia.
        Por isso ela so e montada quando ha um professor escolhido.

        O dialogo fecha **antes** da chamada, e nao depois da resposta: se a
        operacao voltar 403, quem responde e o portao de reautenticacao, com um
        dialogo proprio — e dois dialogos empilhados escondem um ao outro.
      */}
      {chosen === null ? null : (
        <ConfirmDialog
          open={confirming}
          title={t('payroll.batches.open')}
          description={t('payroll.batches.openConfirm', { period, teacher: chosen.displayName })}
          confirmLabel={t('payroll.batches.openAction')}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            gate.run((onReauthRequired) =>
              create.mutate(
                { data: { teacherId: chosen.personId, period } },
                {
                  onSuccess: () => {
                    setChosen(null);
                    onCreated();
                  },
                  onError: onReauthRequired,
                },
              ),
            );
          }}
        />
      )}
    </section>
  );
}

/** Um lote, com a fila de estados e a acao disponivel em cada um. */
export function PayrollBatchPage() {
  const { t } = useTranslation();
  const params = useParams();
  const batchId = params['batchId'] ?? '';
  const meQuery = useGetMe();
  const me = meQuery.data;
  const gate = useReauthGate();

  const detail = useGetPayrollBatch(batchId, { query: { enabled: !!me && batchId.length > 0 } });

  if (!me) return null;

  const data = detail.data;

  return (
    <div className={styles.page}>
      <h1>{t('payroll.batch.title')}</h1>

      {gate.dialog}

      {detail.isError ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}

      {data ? (
        <>
          <p>
            <strong>{data.batch.period}</strong> · <span>{data.batch.teacher.displayName}</span> ·{' '}
            <strong>{formatMoney(data.batch.totalAmount, me.locale)}</strong>
          </p>

          <BatchSteps state={data.batch.state} />

          {data.approval ? (
            <p className={styles.meta}>
              {t('payroll.batch.approvedBy', {
                person: data.approval.approvedBy.displayName,
                date: formatDateInPersonTimezone(
                  data.approval.approvedAt,
                  me.timezone,
                  me.locale,
                ),
              })}
            </p>
          ) : null}

          <SettlementSummary batch={data.batch} />

          <BatchActions
            batch={data.batch}
            me={me}
            gate={gate}
            onChanged={() => void detail.refetch()}
          />

          <section aria-label={t('payroll.batch.items')}>
            <h2>{t('payroll.batch.items')}</h2>
            <p className={styles.meta}>{t('payroll.noStudentNames')}</p>
            <ul className={styles.list}>
              {data.items.map((item) => (
                <AdjustableItem
                  key={item.id}
                  item={item}
                  locale={me.locale}
                  gate={gate}
                  editable={data.batch.state === 'accruing' || data.batch.state === 'in_review'}
                  onAdjusted={() => void detail.refetch()}
                />
              ))}
            </ul>
          </section>
        </>
      ) : null}
    </div>
  );
}

/**
 * A fila dos seis estados.
 *
 * A etapa corrente carrega `aria-current="step"`: quem le por leitor de tela
 * precisa saber onde o lote esta tanto quanto quem enxerga a borda destacada.
 */
function BatchSteps({ state }: { readonly state: PayrollBatchState }) {
  const { t } = useTranslation();
  const current = BATCH_STATES.indexOf(state);

  return (
    <ol className={styles.steps} aria-label={t('payroll.batch.flow')}>
      {BATCH_STATES.map((step, index) => {
        const classes = [
          styles.step,
          index < current ? styles.stepDone : '',
          index === current ? styles.stepCurrent : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <li key={step} className={classes} aria-current={index === current ? 'step' : undefined}>
            {t(`payroll.batchState.${step}`)}
          </li>
        );
      })}
    </ol>
  );
}

function BatchActions({
  batch,
  me,
  gate,
  onChanged,
}: {
  readonly batch: PayrollBatchModel;
  readonly me: Me;
  readonly gate: ReauthGate;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();

  const review = useSubmitBatchForReview();
  const approve = useApproveBatch();
  const schedule = useScheduleBatch();

  const [scheduledFor, setScheduledFor] = useState('');
  const [note, setNote] = useState('');

  // O lote carrega `teacher` justamente para esta decisao ser tomada aqui, e
  // nao virar um 403 depois do clique.
  const ownBatch = batch.teacher.personId === me.id;

  switch (batch.state) {
    case 'accruing':
      return (
        <section aria-label={t('payroll.batch.actions')}>
          <div className={styles.actions}>
            <Button
              variant="primary"
              loading={review.isPending}
              onClick={() =>
                gate.run((onReauthRequired) =>
                  review.mutate(
                    { batchId: batch.id },
                    { onSuccess: onChanged, onError: onReauthRequired },
                  ),
                )
              }
            >
              {t('payroll.batch.submitForReview')}
            </Button>
          </div>
          <ActionFeedback error={review.error} />
        </section>
      );

    case 'in_review':
      return (
        <section aria-label={t('payroll.batch.actions')}>
          <div className={styles.actions}>
            <Field
              label={t('payroll.batch.approvalNote')}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={ownBatch}
            />
            <Button
              variant="primary"
              disabled={ownBatch}
              loading={approve.isPending}
              onClick={() =>
                gate.run((onReauthRequired) =>
                  approve.mutate(
                    { batchId: batch.id, data: { note: note.trim() || null } },
                    { onSuccess: onChanged, onError: onReauthRequired },
                  ),
                )
              }
            >
              {t('payroll.batch.approve')}
            </Button>
          </div>
          {/*
            Botao que some deixa a pessoa procurando; botao desabilitado com o
            motivo escrito ensina a regra.
          */}
          {ownBatch ? <p className={styles.reason}>{t('payroll.batch.ownBatchReason')}</p> : null}
          <ActionFeedback error={approve.error} />
        </section>
      );

    case 'approved':
      return (
        <section aria-label={t('payroll.batch.actions')}>
          <div className={styles.actions}>
            <Field
              label={t('payroll.batch.scheduledFor')}
              type="date"
              value={scheduledFor}
              onChange={(event) => setScheduledFor(event.target.value)}
            />
            <Button
              variant="primary"
              disabled={scheduledFor.length === 0}
              loading={schedule.isPending}
              onClick={() =>
                gate.run((onReauthRequired) =>
                  schedule.mutate(
                    { batchId: batch.id, data: { scheduledFor } },
                    { onSuccess: onChanged, onError: onReauthRequired },
                  ),
                )
              }
            >
              {t('payroll.batch.schedule')}
            </Button>
          </div>
          <ActionFeedback error={schedule.error} />
        </section>
      );

    case 'scheduled':
      return <PaymentAction batch={batch} locale={me.locale} gate={gate} onChanged={onChanged} />;

    case 'paid':
      return <ReceiptAction batch={batch} gate={gate} onChanged={onChanged} />;

    default:
      return (
        <section aria-label={t('payroll.batch.actions')}>
          <p className={styles.meta}>{t('payroll.batch.settledExplained')}</p>
        </section>
      );
  }
}

function localNow(): string {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

/** Os dois meios que a §8.2 nomeia. A tela nao inventa um terceiro. */
const SETTLEMENT_METHODS: readonly string[] = Object.values(SettlementMethod);

/** Maximo do contrato para `reference`. */
const REFERENCE_MAX_LENGTH = 200;

/**
 * Registro do pagamento.
 *
 * O dinheiro sai fora da plataforma; esta operacao registra que ele saiu. A
 * chave de idempotencia nasce **uma vez por montagem** e nao muda entre
 * tentativas: e o que faz repetir a chamada devolver o primeiro resultado em
 * vez de pagar de novo.
 *
 * **"Nao informar" e opcao valida, e nao estado de erro.** O botao continua
 * habilitado com ela escolhida, e nenhuma mensagem aparece ao lado do campo:
 * exigir o meio obrigaria quem registra um pagamento feito por outro caminho a
 * escolher um valor falso da lista, numa tabela que existe justamente para
 * provar como o dinheiro saiu. Ausencia diz "nao perguntei"; valor errado diz
 * outra coisa, e nao se desfaz.
 *
 * **E o ponto sem volta.** `scheduled → paid` nao tem transicao de retorno: o
 * lote pago nao volta a agendado, e a idempotencia protege contra o clique
 * repetido, nao contra o clique errado. Por isso o botao nao paga — ele pergunta,
 * e quem paga e a confirmacao.
 */
function PaymentAction({
  batch,
  locale,
  gate,
  onChanged,
}: {
  readonly batch: PayrollBatchModel;
  readonly locale: string;
  readonly gate: ReauthGate;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [paidAt, setPaidAt] = useState(localNow);
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const pay = usePayBatch({ request: { headers: { 'Idempotency-Key': idempotencyKey } } });

  const trimmedReference = reference.trim();

  // A chamada e montada uma vez e usada pelos dois caminhos possiveis: a
  // confirmacao e a retomada do portao depois da reautenticacao. Duas copias da
  // montagem do corpo seriam duas versoes do que se paga.
  const registerPayment = (): void =>
    gate.run((onReauthRequired) =>
      pay.mutate(
        {
          batchId: batch.id,
          data: {
            paidAt: new Date(paidAt).toISOString(),
            // Campo omitido, e nao string vazia: a API distingue
            // "nao informei" de "informei nada".
            ...(method === '' ? {} : { method }),
            ...(trimmedReference === '' ? {} : { reference: trimmedReference }),
            note: null,
          },
        },
        { onSuccess: onChanged, onError: onReauthRequired },
      ),
    );

  return (
    <section aria-label={t('payroll.batch.actions')}>
      <div className={styles.actions}>
        <Field
          label={t('payroll.batch.paidAt')}
          type="datetime-local"
          value={paidAt}
          onChange={(event) => setPaidAt(event.target.value)}
        />
        {/*
          "Nao informar" entra como opcao da lista, e nao como `placeholder`:
          placeholder se le como "ainda falta escolher", e aqui nao falta.
        */}
        <SelectField
          label={t('payroll.batch.method')}
          value={method}
          options={[
            { value: '', label: t('payroll.batch.methodUnset') },
            ...SETTLEMENT_METHODS.map((value) => ({
              value,
              label: t(`payroll.settlementMethod.${value}`),
            })),
          ]}
          onChange={setMethod}
        />
        <Field
          label={t('payroll.batch.reference')}
          value={reference}
          maxLength={REFERENCE_MAX_LENGTH}
          hint={t('payroll.batch.referenceHint')}
          onChange={(event) => setReference(event.target.value)}
        />
        <Button
          variant="primary"
          disabled={paidAt.length === 0}
          loading={pay.isPending}
          onClick={() => setConfirming(true)}
        >
          {t('payroll.batch.pay')}
        </Button>
      </div>
      <p className={styles.meta}>{t('payroll.batch.payExplained')}</p>
      <p className={styles.meta}>{t('payroll.batch.methodExplained')}</p>
      <ActionFeedback error={pay.error} />

      {/*
        Nome e valor vao por interpolacao porque quem chegou aqui pelo lote
        errado so descobre o erro se a pergunta disser de quem e o dinheiro. A
        frase que ficava aqui antes — a mesma que explica a acao logo acima — era
        verdadeira, mas nao nomeava ninguem e nao dizia que dali nao se volta.

        O dialogo fecha antes da chamada, e nao depois da resposta: um 403 abre o
        dialogo do portao de reautenticacao, e dois dialogos empilhados escondem
        um ao outro. Confirmada a credencial, o portao repete esta mesma acao —
        sem segunda pergunta, porque a intencao ja foi manifestada.
      */}
      <ConfirmDialog
        open={confirming}
        title={t('payroll.batch.pay')}
        description={t('payroll.batch.payConfirm', {
          teacher: batch.teacher.displayName,
          amount: formatMoney(batch.totalAmount, locale),
        })}
        confirmLabel={t('payroll.batch.pay')}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          registerPayment();
        }}
      />
    </section>
  );
}

/**
 * A liquidacao na conferencia do lote.
 *
 * Some inteira enquanto o lote nao foi pago e enquanto nada foi informado: uma
 * linha escrita "Nao informado" em todo lote pago treinaria quem confere a
 * ignora-la, e e justamente essa a linha que precisa ser lida quando aparece.
 */
function SettlementSummary({ batch }: { readonly batch: PayrollBatchModel }) {
  const { t } = useTranslation();
  if (batch.settlementMethod === null && batch.settlementReference === null) return null;

  const methodLabel =
    batch.settlementMethod === null
      ? t('payroll.batch.settlementUnset')
      : SETTLEMENT_METHODS.includes(batch.settlementMethod)
        ? t(`payroll.settlementMethod.${batch.settlementMethod}`)
        : batch.settlementMethod;

  return (
    <table aria-label={t('payroll.batch.settlement')}>
      <tbody>
        <tr>
          <th scope="row">{t('payroll.batch.method')}</th>
          <td>{methodLabel}</td>
        </tr>
        <tr>
          <th scope="row">{t('payroll.batch.reference')}</th>
          <td>{batch.settlementReference ?? t('payroll.batch.settlementUnset')}</td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * Comprovante, em **duas** chamadas.
 *
 * `attachBatchReceipt` emite a URL assinada, o `PUT` envia os bytes, e so
 * entao `confirmBatchReceipt` move `paid → settled`. Uma tela que escrevesse
 * "Comprovado" ao receber a URL assinada e exatamente o defeito que a separacao
 * no contrato existe para impedir: URL emitida e nunca usada deixaria um lote
 * liquidado sem prova nenhuma no bucket.
 *
 * Se o envio falhar, o lote continua Pago e a tela oferece tentar de novo — com
 * uma URL nova, porque a anterior pode ja ter expirado.
 */
function ReceiptAction({
  batch,
  gate,
  onChanged,
}: {
  readonly batch: PayrollBatchModel;
  readonly gate: ReauthGate;
  readonly onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [failed, setFailed] = useState(false);
  const attach = useAttachBatchReceipt();
  const confirm = useConfirmBatchReceipt();

  const send = (): void => {
    if (!file) return;
    setFailed(false);

    // O portao envolve a sequencia inteira: se a emissao da URL for recusada
    // por credencial vencida, o arquivo escolhido continua ali e o envio recomeca
    // do zero apos a confirmacao — com URL nova, porque a anterior pode ter
    // expirado no meio da conversa.
    gate.run((onReauthRequired) =>
      attach.mutate(
        { batchId: batch.id, data: { mimeType: file.type, sizeBytes: file.size, note: null } },
        {
          onSuccess: async (receipt) => {
            try {
              await uploadToSignedUrl(receipt.upload.url, file);
            } catch {
              setFailed(true);
              return;
            }
            confirm.mutate(
              { batchId: batch.id, receiptId: receipt.receiptId },
              { onSuccess: onChanged, onError: () => setFailed(true) },
            );
          },
          onError: (error) => {
            onReauthRequired(error);
            if (!isReauthRequired(error)) setFailed(true);
          },
        },
      ),
    );
  };

  return (
    <section aria-label={t('payroll.batch.receipt')}>
      <h2>{t('payroll.batch.receipt')}</h2>
      <p className={styles.meta}>{t('payroll.batch.receiptExplained')}</p>

      <div className={styles.actions}>
        <Field
          label={t('payroll.batch.receiptFile')}
          type="file"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        <Button
          variant="primary"
          disabled={file === null}
          loading={attach.isPending || confirm.isPending}
          onClick={send}
        >
          {failed ? t('payroll.batch.receiptRetry') : t('payroll.batch.receiptSend')}
        </Button>
      </div>

      {failed ? <Alert tone="error">{t('payroll.batch.receiptFailed')}</Alert> : null}
      <ActionFeedback error={attach.error ?? confirm.error} />
    </section>
  );
}

/**
 * Item com o ajuste ao lado.
 *
 * A justificativa e exigencia do contrato, e nao cortesia da tela: sem ela o
 * servidor responde 422. O botao fica desabilitado enquanto o motivo estiver
 * vazio, para a recusa acontecer antes da requisicao.
 */
function AdjustableItem({
  item,
  locale,
  gate,
  editable,
  onAdjusted,
}: {
  readonly item: PayrollItemView;
  readonly locale: string;
  readonly gate: ReauthGate;
  readonly editable: boolean;
  readonly onAdjusted: () => void;
}) {
  const { t } = useTranslation();
  const [amountCents, setAmountCents] = useState('');
  const [reason, setReason] = useState('');
  const adjust = useCreateItemAdjustment();

  const parsed = Number.parseInt(amountCents, 10);
  const validAmount = Number.isInteger(parsed) && parsed !== 0;
  const canSubmit = validAmount && reason.trim().length > 0;

  return (
    <PayrollItemCard item={item} locale={locale}>
      {editable ? (
        <>
          <div className={styles.actions}>
            {/*
              O campo e em centavos inteiros, e nao em reais com virgula: o
              contrato guarda `amountCents` e a unica conversao para fracao
              acontece na formatacao. Digitar fracao aqui reintroduziria ponto
              flutuante no caminho do dinheiro.
            */}
            <Field
              label={t('payroll.adjustmentAmountCents')}
              type="number"
              value={amountCents}
              onChange={(event) => setAmountCents(event.target.value)}
              hint={
                validAmount
                  ? formatMoney({ amountCents: parsed, currency: 'BRL' }, locale)
                  : t('payroll.adjustmentAmountHint')
              }
            />
            <Field
              label={t('payroll.adjustmentReason')}
              required
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <Button
              variant="secondary"
              disabled={!canSubmit}
              loading={adjust.isPending}
              onClick={() =>
                gate.run((onReauthRequired) =>
                  adjust.mutate(
                    {
                      payrollItemId: item.id,
                      data: {
                        amount: { amountCents: parsed, currency: 'BRL' },
                        reason: reason.trim(),
                      },
                    },
                    {
                      onSuccess: () => {
                        setAmountCents('');
                        setReason('');
                        onAdjusted();
                      },
                      onError: onReauthRequired,
                    },
                  ),
                )
              }
            >
              {t('payroll.adjust')}
            </Button>
          </div>
          <ActionFeedback error={adjust.error} />
        </>
      ) : null}
    </PayrollItemCard>
  );
}
